import  { useEffect, useState } from "react";
import "./NinthReport.css";

const QTag = ({ q, a, impacts }) => (
  <div style={{display:'flex',flexWrap:'wrap',gap:'5px 10px',alignItems:'center',background:'#f0f4ff',border:'1px solid #c7d7fa',borderRadius:'6px',padding:'7px 12px',marginBottom:'12px',fontSize:'12px',lineHeight:1.5}}>
    <span style={{background:'#3b5bdb',color:'#fff',fontWeight:700,padding:'2px 8px',borderRadius:4,fontSize:'10px',whiteSpace:'nowrap'}}>📋 FORM QUESTION</span>
    <span style={{color:'#1e3a8a',fontStyle:'italic'}}>"{q}"</span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#047857',fontWeight:700}}>You answered: <strong>{a}</strong></span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#7c3aed',fontSize:'11px'}}>📊 Impacts: {impacts}</span>
  </div>
);

const NinthReport = ({ res }) => {
  const [teamValue, setTeamValue] = useState("");
  const [cultureValue, setCultureValue] = useState("");
  const [genderServiceValue, setGenderServiceValue] = useState("");
  const [deliveryMethodValue, setDeliveryMethodValue] = useState("");
  const [complaintsValue, setComplaintsValue] = useState("");
  const [availableValue, setAvailableValue] = useState("");

  useEffect(() => {
    if (!res) return;

    setTeamValue(res.team || "");
    setCultureValue(res.culture || "");
    setGenderServiceValue(res.genderService || "");
    setDeliveryMethodValue(res.deliveryMethod || "");
    setComplaintsValue(res.complaintsPercent || "");
    setAvailableValue(res.available || "");
  }, [res]);

  // Helper for case-insensitive compare
  const is = (value, match) =>
    value?.trim().toLowerCase() === match.trim().toLowerCase();

  // 8A - Team Knowledge
  const teamContent = is(teamValue, "-Yes")
    ? "If your team or mentor lacks in-depth knowledge about the idea, you may face challenges such as poor decision-making, weak product–market fit, and reduced investor confidence."
    : "However, if you possess strong domain expertise, you can make faster, data-driven decisions, build customer-focused solutions, and gain investor trust. This knowledge will help our startup grow sustainably and competitively in the market.";

  // 8B - Cultural Alignment
  const cultureContent = is(cultureValue, "_Yes")
    ? "By aligning with users’ cultural, social, and linguistic expectations, you strengthen trust, loyalty, and brand identity – proving that culture truly beats strategy."
    : "You’ll embed cultural and language intelligence into your design and communication – turning inclusivity into a strategic growth advantage.";

  // 8C - Gender Viewpoint
  const genderData = {
    male: {
      coreNeeds: "Efficiency, status, performance, control",
      strategy:
        "Build solutions that improve productivity, tech empowerment, and lifestyle convenience. Example: FinTech, HealthTech, EV mobility.",
      insight:
        "Investors view this as a performance-driven market; easy to scale with measurable ROI."
    },
    female: {
      coreNeeds: "Safety, inclusion, flexibility, empowerment",
      strategy:
        "Focus on trust, safety, access, and personalized communication. Example: FemTech, EduTech, HealthCare, eCommerce.",
      insight:
        "Seen as high-impact and high-loyalty market — strong for grants, CSR, and impact investors."
    },
    others: {
      coreNeeds: "Productivity, fairness, and compliance",
      strategy:
        "Offer gender-inclusive tools for HR, education, healthcare, and social programs. Example: SaaS HR platforms, EdTech, GovTech.",
      insight:
        "Highly favored by institutional and ESG-focused investors; aligns with DEI mandates."
    }
  };

  const selectedGenderData =
    genderData[genderServiceValue?.trim().toLowerCase()] || null;

  // 8D - Delivery Method
  const deliveryMethodContent = is(deliveryMethodValue, "Digital")
    ? "Digital-first models scale faster, cost less, and deliver higher long-term value for investors."
    : "Use when physical delivery is essential and cannot be digitized.";

  // 8E - Complaints Logic (case insensitive)
  const complaintsLower = complaintsValue.toLowerCase();
  let complaintsContent = "";

  if (complaintsLower.includes("10") || complaintsLower.includes("20")) {
    complaintsContent =
      "Fix & improve. Complaints under 30% show manageable issues that can be corrected with improvements.";
  } else if (complaintsLower.includes("30") || complaintsLower.includes("40")) {
    complaintsContent =
      "Re-evaluate & possibly pivot. Complaints between 30–50% indicate deeper structural issues.";
  } else if (
    complaintsLower.includes("more") ||
    complaintsLower.includes("50")
  ) {
    complaintsContent =
      "Deep pivot or rebuild model. High complaints (>50%) signal market rejection.";
  }

  // 8F - Availability
  const availabilityContent = is(availableValue, "+Yes")
    ? "Accessible to customers — low friction, strong fit, and faster growth potential."
    : "Location gap — untapped opportunity, needs strategic rollout to scale.";

  return (
    <div style={{ padding: "22px" }}>
      <div className="ninth-module-container">
        <h3 className="module-title"> <span className="module-prefix">Module 8:</span>  Accessibility</h3>
        <QTag
          q="Does your team have domain expertise? Who do you primarily serve, how is the product delivered, and is it currently accessible to customers?"
          a={`Domain knowledge: ${is(teamValue, "-Yes") ? "Yes" : "No"} · Serves: ${genderServiceValue || "—"} · Delivery: ${deliveryMethodValue || "—"} · Currently accessible: ${is(availableValue, "+Yes") ? "Yes" : "No"}`}
          impacts="Team credibility score, customer segment strategy, delivery model efficiency, market accessibility risk"
        />
        {/* Corner Borders */}
        <span className="corner top-left"></span>
        <span className="corner top-right"></span>
        <span className="corner bottom-left"></span>
        <span className="corner bottom-right"></span>

        {/* 8A */}
        {teamValue && (
          <table className="ninth-module-table">
            <tbody>
              <tr>
                <td className="serial-cell">8 A</td>
                <td>{is(teamValue, "-Yes") ? "Yes" : "No"}</td>
                <td>{teamContent}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* 8B */}
        {cultureValue && (
          <table className="ninth-module-table">
            <tbody>
              <tr>
                <td className="serial-cell">8 B</td>
                <td>{is(cultureValue, "_Yes") ? "Yes" : "No"}</td>
                <td>{cultureContent}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* 8C */}
        {selectedGenderData && (
          <div className="ninth-table-wrapper">
            <table className="ninth-module-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Perspective</th>
                  <th>Core Needs</th>
                  <th>Design / Product Strategy</th>
                  <th>Investor Insight</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="serial-cell">8 C</td>
                  <td>{genderServiceValue} Viewpoint</td>
                  <td>{selectedGenderData.coreNeeds}</td>
                  <td>{selectedGenderData.strategy}</td>
                  <td>{selectedGenderData.insight}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* 8D */}
        {deliveryMethodValue && (
          <table className="ninth-module-table">
            <tbody>
              <tr>
                <td className="serial-cell">8 D</td>
                <td>{deliveryMethodValue}</td>
                <td>{deliveryMethodContent}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* 8E */}
        {complaintsValue && (
          <table className="ninth-module-table">
            <tbody>
              <tr>
                <td className="serial-cell">8 E</td>
                <td>{complaintsValue}</td>
                <td>{complaintsContent}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* 8F */}
        {availableValue && (
          <table className="ninth-module-table">
            <tbody>
              <tr>
                <td className="serial-cell">8 F</td>
                <td>{is(availableValue, "+Yes") ? "Yes" : "No"}</td>
                <td>{availabilityContent}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default NinthReport;
